import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, GraduationCap, LineChart, Cpu, Briefcase, Users } from "lucide-react";
import { api, openExternal, openPartners } from "../lib/site";
import PageHero from "../components/site/PageHero";
import { Reveal } from "../components/site/Reveal";
import { Overline, PrimaryButton, GoldButton } from "../components/site/Primitives";

const CATEGORY_ICON = {
  "Vocational Education": GraduationCap,
  "Small Business Consulting": LineChart,
  "AI Integration": Cpu,
  "Workforce Development": Briefcase,
  "Professional Development": Users,
};

// Programs sourced from backend where possible, plus workforce entries defined locally.
const WORKFORCE_PROGRAMS = [
  {
    id: "prg-workforce-workshops",
    title: "Workforce Workshops",
    subtitle: "Custom cohorts for orgs & agencies",
    description:
      "On-site or virtual workshops built for workforce development orgs, nonprofits, and government partners — career readiness, industry certifications, and job-skills training tailored to your funding goals.",
    price: "Custom pricing",
    category: "Workforce Development",
    ctaLabel: "Request Proposal",
    ctaAction: "partners",
  },
  {
    id: "prg-ai-workshop",
    title: "AI for Small Business",
    subtitle: "Half-day & full-day workshops",
    description:
      "Hands-on AI training for entrepreneurs and small business teams — automation, marketing systems, and everyday tools that free up 10+ hours a week.",
    price: "From $497",
    category: "AI Integration",
    ctaLabel: "Book Workshop",
    ctaAction: "partners",
  },
  {
    id: "prg-curriculum",
    title: "Curriculum & Program Design",
    subtitle: "Instructional design services",
    description:
      "Full curriculum builds, workshop facilitation, compliance training, and instructional design for educational institutions and workforce partners.",
    price: "Custom pricing",
    category: "Professional Development",
    ctaLabel: "Start a Project",
    ctaAction: "partners",
  },
];

const mapProgram = (p) => ({
  id: p.id,
  title: p.title,
  subtitle: p.subtitle,
  description: p.description,
  price: p.price,
  category: p.track === "trainee" ? "Vocational Education" : p.title.toLowerCase().includes("ai") ? "AI Integration" : "Small Business Consulting",
  ctaLabel: p.checkout_url ? "Enroll" : "Request Info",
  ctaAction: p.checkout_url ? "external" : "partners",
  checkout_url: p.checkout_url,
});

export default function Programs() {
  const [beauty, setBeauty] = useState([]);
  useEffect(() => { api.get("/programs").then((r) => setBeauty(r.data.map(mapProgram))).catch(() => {}); }, []);

  const all = [...beauty, ...WORKFORCE_PROGRAMS];

  return (
    <div data-testid="programs-page">
      <PageHero
        overline="All programs"
        title="Programs that build careers."
        subtitle="From licensing courses to AI workshops and workforce cohorts — pick the path that fits your goal or your organization's mandate."
      />

      <section className="bg-brand-cream py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid gap-6 md:grid-cols-2">
            {all.map((p, i) => {
              const Icon = CATEGORY_ICON[p.category] || GraduationCap;
              return (
                <Reveal key={p.id} delay={i * 0.05}>
                  <div data-testid={`program-card-${p.id}`} className="h-full bg-white rounded-3xl border border-brand-ink/5 p-9 md:p-10 flex flex-col">
                    <div className="flex items-start justify-between gap-4">
                      <div className="h-12 w-12 rounded-2xl bg-brand-blush flex items-center justify-center text-brand-rose">
                        <Icon size={22} strokeWidth={1.6} />
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-brand-rose text-right">{p.category}</span>
                    </div>
                    <h2 className="font-serif text-3xl md:text-[34px] text-brand-ink tracking-tight mt-6 leading-tight">{p.title}</h2>
                    {p.subtitle && <p className="text-brand-ink/60 text-sm mt-2">{p.subtitle}</p>}
                    <p className="text-brand-ink/70 font-light mt-4 leading-relaxed">{p.description}</p>
                    <div className="mt-auto pt-6 flex items-end justify-between gap-4">
                      <span className="font-serif text-brand-rose text-2xl">{p.price}</span>
                      {p.ctaAction === "external" ? (
                        <button
                          type="button"
                          onClick={() => openExternal(p.checkout_url)}
                          data-testid={`program-cta-${p.id}`}
                          className="inline-flex items-center gap-1.5 text-sm tracking-wide text-brand-ink border-b border-brand-gold pb-0.5 hover:text-brand-rose transition-colors"
                        >
                          {p.ctaLabel} <ArrowUpRight size={14} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={openPartners}
                          data-testid={`program-cta-${p.id}`}
                          className="inline-flex items-center gap-1.5 text-sm tracking-wide text-brand-ink border-b border-brand-gold pb-0.5 hover:text-brand-rose transition-colors"
                        >
                          {p.ctaLabel} <ArrowUpRight size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-brand-blush py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center flex flex-col items-center">
          <Overline className="mb-5">Don&apos;t see what you need?</Overline>
          <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl tracking-tight">Custom cohorts & contracts welcome.</h2>
          <p className="text-brand-ink/70 font-light mt-5 max-w-lg">
            We build custom vocational and workforce programs for institutions,
            agencies, and employers. Tell us your goal — we&apos;ll build the program.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <PrimaryButton onClick={openPartners} data-testid="programs-partner-btn">Partner With Us</PrimaryButton>
            <GoldButton as="link" to="/contact" data-testid="programs-contact-btn">Contact</GoldButton>
          </div>
        </div>
      </section>
    </div>
  );
}
